/**
 * `/logout?return_to=<stock page>` — sign out of the portal, then go back.
 *
 * The Stock site's logout lands here so one sign-out ends both sessions
 * (lib/stockSession.ts). The portal session is ended with the normal
 * POST /api/portal/auth/logout (Origin + CSRF checked as usual); a visitor
 * who is not signed in is just passed on. `return_to` is followed only to the
 * paired Stock site; anything else ends on /login.
 */
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { portalApi } from '../lib/apiClient';
import { seedCsrfAndProbeSession } from '../lib/auth';
import { stockReturnTo } from '../lib/stockSession';

export function LogoutPage(): JSX.Element {
  const [params] = useSearchParams();
  const target = stockReturnTo(params.get('return_to')) ?? '/login';

  useEffect(() => {
    const ctrl = new AbortController();
    void (async () => {
      if ((await seedCsrfAndProbeSession(ctrl.signal)) === 'authenticated') {
        try {
          await portalApi('/api/portal/auth/logout', {
            method: 'POST',
            body: {},
            signal: ctrl.signal,
          });
        } catch {
          // Still leave: the Stock side is already signed out.
        }
      }
      if (!ctrl.signal.aborted) window.location.replace(target);
    })();
    return () => ctrl.abort();
  }, [target]);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        textAlign: 'center',
      }}
    >
      <p style={{ margin: 0 }}>Signing out…</p>
    </div>
  );
}
